import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/locale";
import { ensureUrl } from "@/lib/url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Sofa, Plus, Trash2, ExternalLink, Camera } from "lucide-react";
import { imageUrl } from "@/components/ImageUploader";
import { toast } from "sonner";

const CATEGORIES = ["Furniture", "Decor", "Appliance", "Electronics", "Outdoor", "Other"];

export default function Wishlist() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { name: "", category: "Furniture", priority: "medium", estimated_price: "", url: "", notes: "" };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => { const { data } = await api.get("/wishlist"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      const payload = {
        ...form,
        url: ensureUrl(form.url),
        estimated_price: form.estimated_price ? parseFloat(form.estimated_price) : null,
      };
      await api.post("/wishlist", payload);
      setOpen(false); setForm(blank); toast.success("Added to wishlist"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/wishlist/${id}`); load(); };

  const priorityRing = {
    high: "ring-hearth-coral/30 bg-hearth-coral/8",
    medium: "ring-hearth-amber/30 bg-hearth-amber/10",
    low: "ring-hearth-sage/30 bg-hearth-sage/8",
  };

  return (
    <div>
      <PageHeader
        eyebrow="Soon, maybe"
        title="Wishlist"
        subtitle="New furniture, gadgets, and house projects to remember."
        action={
          <Button data-testid="wish-add-button" onClick={() => setOpen(true)}
            className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-6 h-11">
            <Plus size={16} className="mr-1.5" /> Add idea
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Sofa} title="Your dream list is empty." message="Drop in the things you've been eyeing — a new sofa, a coffee machine, that rug…" />
      ) : (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-5 space-y-5">
          {items.map((it) => (
            <div key={it.id} data-testid={`wish-card-${it.id}`}
                 className={`warm-card overflow-hidden break-inside-avoid fade-up ring-1 ${priorityRing[it.priority]}`}>
              {it.image_path ? (
                <div className="h-44 bg-hearth-stone overflow-hidden">
                  <img src={imageUrl(it.image_path)} alt={it.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="h-44 bg-hearth-stone overflow-hidden">
                  <img
                    src={`https://source.unsplash.com/400x300/?${encodeURIComponent(it.name)},${encodeURIComponent(it.category || "product")}`}
                    alt={it.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                    data-testid={`wish-auto-photo-${it.id}`}
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="caption">{it.category}</p>
                    <h3 className="font-display text-2xl text-hearth-ink mt-1 leading-tight">{it.name}</h3>
                  </div>
                  <span className={`chip ${
                    it.priority === "high" ? "bg-hearth-coral/15 text-hearth-coral" :
                    it.priority === "medium" ? "bg-hearth-amber/20 text-[#7A5320]" :
                    "bg-hearth-sage/15 text-hearth-sageDark"
                  }`}>{it.priority}</span>
                </div>
                {it.estimated_price ? <div className="mt-3 text-hearth-ink font-mono">~ {money(it.estimated_price)}</div> : null}
                {it.notes && <p className="text-sm text-hearth-mute mt-3 leading-relaxed">{it.notes}</p>}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {it.url && (
                    <a href={ensureUrl(it.url)} target="_blank" rel="noreferrer" data-testid={`wish-url-${it.id}`} className="inline-flex items-center gap-1 text-sm text-hearth-clay hover:underline">
                      View <ExternalLink size={13} />
                    </a>
                  )}
                  <label className="pill-btn border border-hearth-line bg-hearth-paper hover:bg-hearth-stone text-hearth-mute hover:text-hearth-ink inline-flex items-center gap-1.5 cursor-pointer" data-testid={`wish-photo-${it.id}`}>
                    <Camera size={12} /> {it.image_path ? "Change" : "Photo"}
                    <input type="file" hidden accept="image/*" onChange={async (e) => {
                      const file = e.target.files?.[0]; e.target.value = "";
                      if (!file) return;
                      const fd = new FormData(); fd.append("file", file);
                      try { await api.post(`/wishlist/${it.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); load(); }
                      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }} />
                  </label>
                  <button data-testid={`wish-delete-${it.id}`} onClick={() => remove(it.id)} className="ml-auto p-2 rounded-md text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-3xl">New wishlist item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="caption text-hearth-mute">Name</Label>
              <Input data-testid="wish-form-name" className="input-warm mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Linen sofa" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="wish-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption text-hearth-mute">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Estimated price</Label>
                <Input data-testid="wish-form-price" type="number" step="0.01" className="input-warm mt-1.5 h-11" value={form.estimated_price} onChange={(e) => setForm({ ...form, estimated_price: e.target.value })} />
              </div>
              <div>
                <Label className="caption text-hearth-mute">Link</Label>
                <Input data-testid="wish-form-url" className="input-warm mt-1.5 h-11" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" />
              </div>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Notes</Label>
              <Textarea data-testid="wish-form-notes" className="input-warm mt-1.5" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="wish-form-save" onClick={save} className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
